import { lazy, Suspense } from 'react';
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
} from 'react-router-dom';

import { WorkspaceContextProvider } from './workspace/workspace-context-provider';
import { useWorkspaceContext } from './workspace/workspace-context';
import { AccountContextProvider, RedirectToAccount } from './lib/accounts/account-context-provider';
import { LoginPage } from './public/login';
import { SignupPage } from './public/signup';
import { LogoutPage } from './public/logout';
import { VerifyEmailPage } from './public/verify-email';
import { ToastContextProvider } from './components/toast/toast-context-provider';
import { ConfirmEmailPage } from './public/confirm-email';
import { LayoutContextProvider } from './components/layout/layout-context-provider';
import { SidebarLayout } from './components/layout';
import { GradientLayout } from './components/gradient-layout';
import { UpdateNotification } from './components/update-notification';
import { RedirectToAlertExploration } from './alerts/redirect-to-alert-exploration';
import { addNextUrlParam, getNextUrl } from './public/utils/url';
import { isNil, notNil } from './lib/utils';
import { FullPageLoader } from './components/loader';

const Explore = lazy(() => import('./explore').then((m) => ({ default: m.Explore })));
const AiQueryHistoryPage = lazy(() =>
  import('./ai-query-history').then((m) => ({ default: m.AiQueryHistoryPage })),
);
const AccountSettingsPage = lazy(() =>
  import('./settings').then((m) => ({ default: m.AccountSettingsPage })),
);
const IntegrationCallbackPage = lazy(() =>
  import('./settings/integrations/integration-callback').then((m) => ({
    default: m.IntegrationCallbackPage,
  })),
);
const OnboardingDatabaseStep = lazy(() =>
  import('./onboarding/step-database-setup').then((m) => ({ default: m.OnboardingDatabaseStep })),
);
const OnboardingComplete = lazy(() =>
  import('./onboarding/step-complete').then((m) => ({ default: m.OnboardingComplete })),
);
const OnboardingStart = lazy(() =>
  import('./onboarding/step-start').then((m) => ({ default: m.OnboardingStart })),
);
const OnboardingLegal = lazy(() =>
  import('./onboarding/step-legal').then((m) => ({ default: m.OnboardingLegal })),
);
const WelcomeSurveyStep = lazy(() =>
  import('./onboarding/step-welcome-survey').then((m) => ({ default: m.WelcomeSurveyStep })),
);
const OnboardingSetupCallStep = lazy(() =>
  import('./onboarding/step-setup-call').then((m) => ({ default: m.OnboardingSetupCallStep })),
);
const GitHubCallbackRedirect = lazy(() =>
  import('./settings/integrations/github/github-callback-redirect').then((m) => ({
    default: m.GitHubCallbackRedirect,
  })),
);

import './styles/app.scss';
import { ErrorBoundary } from './lib/error/boundary';
import { GenericFallback } from './lib/error/generic-fallback';

const App = () => (
  <ErrorBoundary fallback={<GenericFallback fullscreen />}>
    <WorkspaceContextProvider>
      <ToastContextProvider>
        <RouterProvider
          router={createBrowserRouter(
            createRoutesFromElements(
              <>
                <Route
                  path="integrations/callback/github"
                  element={
                    <Suspense fallback={<FullPageLoader />}>
                      <GitHubCallbackRedirect />
                    </Suspense>
                  }
                />
                <Route element={<GradientLayout />}>
                  <Route element={<Unauthenticated />}>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                  </Route>
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                  <Route path="/logout" element={<LogoutPage />} />
                </Route>
                <Route element={<Authenticated />}>
                  <Route
                    path=":account/*"
                    element={
                      <AccountContextProvider>
                        <Routes>
                          <Route
                            element={
                              <LayoutContextProvider>
                                <SidebarLayout />
                                {import.meta.env.PROD && <UpdateNotification />}
                              </LayoutContextProvider>
                            }>
                            <Route
                              path="/alerts/:alertConfigurationId/exploration"
                              element={<RedirectToAlertExploration />}
                            />
                            <Route
                              path="explore/*"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <Explore />
                                </Suspense>
                              }
                            />
                            <Route
                              path="history"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <AiQueryHistoryPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="settings/integrate/:integration"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <IntegrationCallbackPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="settings/:view?/*"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <AccountSettingsPage />
                                </Suspense>
                              }
                            />
                          </Route>
                          <Route path="/onboarding" element={<GradientLayout showLogout />}>
                            <Route
                              index
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <OnboardingStart />
                                </Suspense>
                              }
                            />
                            <Route
                              path="legal"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <OnboardingLegal />
                                </Suspense>
                              }
                            />
                            <Route
                              path="welcome-survey"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <WelcomeSurveyStep />
                                </Suspense>
                              }
                            />
                            <Route
                              path="setup-call"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <OnboardingSetupCallStep />
                                </Suspense>
                              }
                            />
                            <Route
                              path="connection"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <OnboardingDatabaseStep />
                                </Suspense>
                              }
                            />
                            <Route
                              path="complete"
                              element={
                                <Suspense fallback={<FullPageLoader />}>
                                  <OnboardingComplete />
                                </Suspense>
                              }
                            />
                          </Route>
                          <Route path="*" element={<Navigate to="/explore" replace />} />
                        </Routes>
                      </AccountContextProvider>
                    }
                  />
                  <Route index element={<RedirectToAccount />} />
                  <Route path="explore/*" element={<RedirectToAccount />} />
                  <Route path="settings/*" element={<RedirectToAccount />} />
                </Route>
              </>,
            ),
          )}
        />
      </ToastContextProvider>
    </WorkspaceContextProvider>
  </ErrorBoundary>
);

const Authenticated = () => {
  const { user } = useWorkspaceContext();
  const location = useLocation();

  if (isNil(user)) {
    return <Navigate to={addNextUrlParam('/login', location)} />;
  }

  if (location.pathname !== '/verify-email' && !user.emailVerified) {
    return <Navigate to={'/verify-email'} />;
  }

  return <Outlet />;
};

export const Unauthenticated = () => {
  const { user, accounts } = useWorkspaceContext();
  const location = useLocation();

  if (
    notNil(user) &&
    user.emailVerified &&
    (accounts.length > 0 || location.pathname !== '/signup')
  ) {
    return <Navigate to={getNextUrl(location)} />;
  }

  return <Outlet />;
};

export default App;
